/**
 * Vehicle Lookup Hook
 *
 * Provides a mutation for looking up vehicle data from external registry services.
 */

import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface VehicleLookupResult {
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  bodyType: string | null;
  color: string | null;
  doors: number | null;
  seats: number | null;
  fuelType: string | null;
  engineDisplacementCc: number | null;
  enginePowerKw: number | null;
  transmission: string | null;
  driveType: string | null;
  curbWeightKg: number | null;
  grossWeightKg: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  vehicleClass: string | null;
  vehicleGroup: string | null;
  co2EmissionsGkm: number | null;
  euroEmissionClass: string | null;
  firstRegistrationDate: string | null;
  rawData: Record<string, unknown>;
  fetchedAt: string;
  provider: string;
}

interface LookupParams {
  registrationNumber: string;
  force?: boolean;
  vin?: string;
}

export function useVehicleLookup() {
  return useMutation({
    mutationFn: ({ registrationNumber, force, vin }: LookupParams) => {
      const params = new URLSearchParams();
      if (force) params.set('force', 'true');
      if (vin) params.set('vin', vin);
      const qs = params.toString();
      return api.get<VehicleLookupResult>(
        `/vehicles/lookup/${encodeURIComponent(registrationNumber)}${qs ? `?${qs}` : ''}`,
      );
    },
  });
}
